/**
 * The one way this app asks for Web Storage (ui#618).
 *
 * **Never probe the bare `localStorage`/`sessionStorage` global.** Node has Web Storage globals of its
 * own now, so `typeof localStorage !== "undefined"` no longer answers "am I in a browser". It answers
 * "does THIS Node expose Web Storage" — a question whose answer changed under us. Measured with no
 * flags: `node -e 'typeof localStorage, typeof sessionStorage'` gives `undefined undefined` on
 * v24.18.0 (what CI pins) and `undefined object` on v26.4.0 (what this repo's developers run), the
 * `undefined` there being the value a WARNING accessor returns rather than an absent global:
 *
 * - `localStorage` at least announces itself — reading it prints one `ExperimentalWarning` per
 *   process and yields `undefined` unless the process was started with `--localstorage-file`. That
 *   warning was 113 lines ahead of every local `vitest run` and one line of every local build, which
 *   is the only reason this whole class of probe was noticed at all (ui#618).
 * - `sessionStorage` is the worse of the two: Node's is a fully working, silent, process-wide
 *   in-memory Storage. A prerender-time probe reports "browser" with nothing to warn anyone, reads
 *   answer, writes succeed — and every route rendered by that one Node process shares the store.
 *
 * `window` is the honest question, because Node has none in either case. These helpers return the
 * `Storage` or `null`, so a caller branches once on the value it is about to use instead of on a
 * separate boolean that can drift from it. Access itself is guarded too: a privacy-locked or
 * sandboxed browser throws on the property, which reads here as "no storage".
 *
 * React-free by mandate (CLAUDE.md's presentation/logic split) — this is the storage seam that
 * services, the persistence layer and components all share.
 */

function storageOrNull(pick: (host: Window & typeof globalThis) => Storage | null | undefined): Storage | null {
	if (typeof window === "undefined") {
		return null;
	}
	try {
		return pick(window) ?? null;
	} catch {
		return null;
	}
}

/** The browser's `localStorage`, or `null` outside a browser (SSG prerender, node-env tests, workers). */
export function browserLocalStorage(): Storage | null {
	return storageOrNull((host) => host.localStorage);
}

/** The browser's `sessionStorage`, or `null` outside a browser. See {@link browserLocalStorage}. */
export function browserSessionStorage(): Storage | null {
	return storageOrNull((host) => host.sessionStorage);
}
